import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './SecureAttachment.scss';

interface SecureAttachmentProps {
  fileUrl: string;
  fileName: string;
  contentType: string;
  fileSize: number;
  isSent: boolean;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('gcxp_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};


/**
 * A document the guest sent in the chat was moved to their reservation's Documents (one copy only), or a
 * card photo was removed. The chat keeps this placeholder.
 */
function FiledPlaceholder({ fileUrl, fileName }: { fileUrl: string; fileName?: string }) {
  const removed = fileUrl.startsWith('removed:');
  return (
    <div className="message-attachment-filed" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px', borderRadius: 10, background: 'rgba(0,0,0,0.05)', fontSize: 14 }}>
      <span>{removed ? '🗑️' : '🔒'}</span>
      <span>{fileName || (removed ? 'Removed' : 'Added to your reservation')}</span>
    </div>
  );
}

export default function SecureAttachment(props: SecureAttachmentProps) {
  if (props.contentType === 'application/x-myroomhq-filed') return <FiledPlaceholder fileUrl={props.fileUrl} fileName={props.fileName} />;
  return <SecureAttachmentFile {...props} />;
}

function SecureAttachmentFile({ fileUrl, fileName, contentType, fileSize, isSent }: SecureAttachmentProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const isImage = contentType?.startsWith('image/');
  const isVideo = contentType?.startsWith('video/');
  const isMedia = isImage || isVideo;

  useEffect(() => {
    let revoked = false;

    const loadFile = async () => {
      try {
        let url = fileUrl;
        try { url = new URL(fileUrl).pathname; } catch { /* already relative */ }
        if (url.startsWith('/api/v1/admin/media/')) {
          url = '/api/v1/guest/messages/media/' + url.substring('/api/v1/admin/media/'.length);
        }
        const fullUrl = `${API_BASE_URL}${url}`;
        const res = await fetch(fullUrl, { headers: getAuthHeaders(), cache: 'no-store' });
        if (!res.ok) throw new Error('Failed to load');
        const blob = await res.blob();
        if (revoked) return;
        setBlobUrl(URL.createObjectURL(blob));
      } catch {
        if (!revoked) setError(true);
      } finally {
        if (!revoked) setLoading(false);
      }
    };

    loadFile();

    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUrl]);

  // Close modal on Escape key
  useEffect(() => {
    if (!modalOpen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setModalOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [modalOpen]);

  const handleDownload = () => {
    if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName || 'download';
      a.click();
    }
  };

  if (loading) {
    // For media, show a placeholder the same size as the final thumbnail to prevent layout shift
    if (isMedia) {
      return (
        <div className="secure-attachment-media-placeholder">
          <span className="secure-attachment-spinner" />
        </div>
      );
    }
    return (
      <div className={`secure-attachment-loading ${isSent ? 'sent' : 'received'}`}>
        <span className="secure-attachment-spinner" />
        <span className="secure-attachment-name">{fileName}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`secure-attachment-error ${isSent ? 'sent' : 'received'}`}>
        📄 <span className="secure-attachment-name">{fileName}</span>
        <span className="secure-attachment-failed">Failed</span>
      </div>
    );
  }

  // Image thumbnail
  if (isImage && blobUrl) {
    return (
      <>
        <img
          src={blobUrl}
          alt={fileName}
          className="secure-attachment-image"
          onClick={() => setModalOpen(true)}
        />
        {modalOpen && createPortal(
          <div className="media-modal-overlay" onClick={() => setModalOpen(false)}>
            <div className="media-modal-content" onClick={e => e.stopPropagation()}>
              <button className="media-modal-close" onClick={() => setModalOpen(false)}>✕</button>
              <img src={blobUrl} alt={fileName} className="media-modal-image" />
              <div className="media-modal-footer">
                <span className="media-modal-name">{fileName}</span>
                <button className="media-modal-download" onClick={handleDownload}>⬇ Download</button>
              </div>
            </div>
          </div>
        , document.body)}
      </>
    );
  }

  // Video thumbnail
  if (isVideo && blobUrl) {
    return (
      <>
        <div className="secure-attachment-video-thumb" onClick={() => setModalOpen(true)}>
          <video src={blobUrl} className="secure-attachment-video" preload="metadata" />
          <div className="secure-attachment-play-icon">▶</div>
        </div>
        {modalOpen && createPortal(
          <div className="media-modal-overlay" onClick={() => setModalOpen(false)}>
            <div className="media-modal-content" onClick={e => e.stopPropagation()}>
              <button className="media-modal-close" onClick={() => setModalOpen(false)}>✕</button>
              <video src={blobUrl} className="media-modal-video" controls autoPlay />
              <div className="media-modal-footer">
                <span className="media-modal-name">{fileName}</span>
                <button className="media-modal-download" onClick={handleDownload}>⬇ Download</button>
              </div>
            </div>
          </div>
        , document.body)}
      </>
    );
  }

  // Non-media file
  return (
    <button onClick={handleDownload} className={`secure-attachment-file ${isSent ? 'sent' : 'received'}`}>
      <span>📄</span>
      <span className="secure-attachment-name">{fileName}</span>
      <span className="secure-attachment-size">{formatFileSize(fileSize)}</span>
      <span>⬇</span>
    </button>
  );
}
